import React, { useState } from 'react';
import {
  FDBodyText, FDIcon, FDLozenge, FDSwitchInput,
  FDTable, FDTableBody, FDTableCell, FDTableHead, FDTableRow, FDTooltip,
} from '@facet-wealth/design';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { getThresholdColorMap } from 'Consts/thresholds';
import classNames from 'classnames';
import { getBorder, getBorderRadius } from 'Components/utils';
import { MUTATION_COMPARISON_COLORS } from 'Organisms/retirementWithdrawalStrategyTable/consts';
import { dataAttrs, testAttr } from 'Helpers/Testing';
import { SortButton } from './sortButton';
import { useSortedMutations } from './useSortedMutations';

const MAX_MUTATIONS_COMPARABLE_AT_ONCE = MUTATION_COMPARISON_COLORS.length;

const useStyles = buildUseStyles('RetirementWithdrawalStrategyTable', (theme) => ({
  root: {
    '& thead': {
      ...getBorder(theme, { bottom: true, color: theme.palette.facet.lightGray2 }),
    },
  },
  tableCell: {
    // Add spacing between elements without using flex so that when text wraps in the middle element the final element doesn't end up on the far right, but instead at the end of the last line of text
    '& > * + *': {
      marginLeft: `${theme.spacing(1)}px !important`, // Needed to overwrite MUI
    },
    // Due to the above, a few more tweaks have to be made
    '& > *': {
      verticalAlign: 'middle',
    },
    '& > p': {
      display: 'inline-block',
    },
  },
  hoverable: {
    '&:hover': {
      cursor: 'pointer',
    },
    '& p': {
      textDecoration: 'underline',
    },
  },
  circle: {
    display: 'inline-flex',
    ...getBorderRadius(theme.radius.full, { all: true }),
    '& > div': {
      display: 'inline-flex',
      '& > .FD-MuiSvgIcon-root': {
        color: `${theme.palette.common.white} !important`, // Needed to overwrite MUI
      },
    },
    // Handle MUI icons
    '& > .FD-MuiSvgIcon-root': {
      color: `${theme.palette.common.white} !important`, // Needed to overwrite MUI
    },
  },
  ...(Object.fromEntries(Object.entries(getThresholdColorMap(theme)).map(([colorName, color]) => ([
    `circle${colorName}`,
    { backgroundColor: color },
  ])))),
}));

export function RetirementWithdrawalStrategyTable({
  mutations, chosen, onChoose, showComparison, mutationsToCompare, setMutationsToCompare,
}) {
  const classes = useStyles();
  const [hoveringOverMutationID, setHoveringOverMutationID] = useState();
  const [sortBy, setSortBy] = useState();
  const [sortAscending, setSortAscending] = useState(true);
  const sortedMutations = useSortedMutations({ mutations, sortBy, sortAscending });
  if (!mutations || mutations.length === 0) {
    return <FDBodyText value="No info" />;
  }

  const columnToDescription = {};
  mutations.forEach((mutation) => {
    mutation.attr('results')?.resources()?.list()?.forEach((item) => {
      columnToDescription[item.attr('title')] = item.attr('description') ?? '';
    });
  });
  const columns = Object.keys(columnToDescription);

  return (
    <div className={classes.root}>
      <FDTable>
        <FDTableHead>
          <FDTableRow>
            <FDTableCell>
              <div className={classes.tableCell}>
                <FDBodyText value={mutations[0].metaField('title')?.attr('prompt')} />
                <SortButton column="title" sortBy={sortBy} setSortBy={setSortBy} sortAscending={sortAscending} setSortAscending={setSortAscending} />
              </div>
            </FDTableCell>
            {columns.map((column) => (
              <FDTableCell key={column}>
                <div className={classes.tableCell}>
                  <FDBodyText value={column} />
                  {columnToDescription[column] ? <FDTooltip placement="top" title={columnToDescription[column]}><FDIcon size="sm" icon="infoFilled" /></FDTooltip> : null}
                  <SortButton column={column} sortBy={sortBy} setSortBy={setSortBy} sortAscending={sortAscending} setSortAscending={setSortAscending} />
                </div>
              </FDTableCell>
            ))}
            {showComparison ? (
              <FDTableCell>
                <div className={classes.tableCell}>
                  <FDBodyText value="Compare" />
                </div>
              </FDTableCell>
            ) : null}
          </FDTableRow>
        </FDTableHead>
        <FDTableBody>
          {sortedMutations.map((mutation) => {
            const resultsArray = mutation.attr('results')?.resources()?.list() ?? [];
            const resultsObject = Object.fromEntries(resultsArray.map((result) => ([result.attr('title'), result])));
            const isBeingCompared = showComparison && mutationsToCompare.includes(mutation.id());
            let compareSwitchDisabledReason = '';
            if (showComparison && mutationsToCompare.length === MAX_MUTATIONS_COMPARABLE_AT_ONCE && !isBeingCompared) {
              compareSwitchDisabledReason = `You can only compare ${MAX_MUTATIONS_COMPARABLE_AT_ONCE} strategies at once. Deselect another strategy to view this one.`;
            }
            if (showComparison && mutationsToCompare.length === 1 && isBeingCompared) {
              compareSwitchDisabledReason = 'You must have at least 1 strategy selected to compare with.';
            }
            let mutationColor = 'textPrimary';
            if (isBeingCompared) {
              mutationColor = MUTATION_COMPARISON_COLORS[mutationsToCompare.indexOf(mutation.id())];
            }
            if (!showComparison && chosen === mutation.id()) {
              [mutationColor] = MUTATION_COMPARISON_COLORS;
            }
            return (
              <FDTableRow key={mutation.id()}>
                <FDTableCell>
                  <div
                    className={classNames(classes.tableCell, { [classes.hoverable]: onChoose && hoveringOverMutationID === mutation.id() })}
                    onMouseEnter={() => setHoveringOverMutationID(mutation.id())}
                    // If you stop hovering we need to change state, unless it's already updated to a different mutation ID that's being hovered over
                    onMouseLeave={() => setHoveringOverMutationID(id => (id === mutation.id() ? undefined : id))}
                    role="button"
                    tabIndex={0}
                    onClick={() => onChoose(mutation.id())}
                    onKeyDown={e => (e.key === 'Enter' || e.keyCode === 13 ? onChoose(mutation.id()) : undefined)}
                  >
                    {onChoose ? (
                      <FDIcon
                        icon={chosen === mutation.id() || hoveringOverMutationID === mutation.id() ? 'circle' : 'circleOutline'}
                        size="sm"
                        color={mutationColor}
                      />
                    ) : null}
                    <FDBodyText value={mutation.attr('title')} />
                    {mutation.isTruthy('active') ? <FDLozenge color="success" label="Active" /> : null}
                    {(mutation.attr('tags') ?? []).map(award => <FDLozenge key={award} color="info" label={award} />)}
                  </div>
                </FDTableCell>
                {columns.map((column) => (
                  <FDTableCell key={column}>
                    {resultsObject[column] ? (
                      <div className={classes.tableCell}>
                        <FDBodyText value={resultsObject[column].attr('valueFormatted')} />
                        {resultsObject[column].attr('thresholdIcon') ? (
                          <div className={classNames(classes.circle, classes[`circle${resultsObject[column].attr('thresholdIconColor')}`])}>
                            <FDIcon icon={resultsObject[column].attr('thresholdIcon')} size="sm" />
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </FDTableCell>
                ))}
                {showComparison ? (
                  <FDTableCell>
                    <div className={classes.tableCell}>
                      <FDTooltip title={compareSwitchDisabledReason} placement="top">
                        <FDSwitchInput
                          {...dataAttrs(isBeingCompared ? 'disable-comparison' : 'enable-comparison', { [testAttr.dataResourceId]: mutation.id() })}
                          value={isBeingCompared}
                          onChange={shouldInclude => setMutationsToCompare(ids => {
                            // Filter out the ID, and if we should included it, then add it back in
                            const newIDs = ids.filter(id => id !== mutation.id());
                            if (shouldInclude) {
                              newIDs.push(mutation.id());
                            }
                            return newIDs;
                          })}
                          disabled={!!compareSwitchDisabledReason}
                        />
                      </FDTooltip>
                    </div>
                  </FDTableCell>
                ) : null}
              </FDTableRow>
            );
          })}
        </FDTableBody>
      </FDTable>
    </div>
  );
}
